import * as React from "react";
import * as _ from "lodash";
import { useMappedState } from "redux-react-hook";

import {
  getDeliveryCutoff,
  getDeliveryRoute,
  getDeliveryWeekLeadTimes,
  getMinimumOrderQuantitySetting,
  getMinimumOrderValue,
  getOrderMode
} from "src/core/drops/utils/inherited-settings";
import {
  orderMinimumQuantityMet,
  orderMinimumValueNotMet,
  productMinimumQuantityNotMet,
  canListOrderProduct,
  poNumberIsValid,
  validateAgainstMaxPackSizeConstraints
} from "src/core/orders/utils/orders";
import { useDrop } from "src/core/drops/hooks";
import { useValidAgainstProductRelatedGroups } from "src/core/orders/hooks/validation";
import { ErrorLevel } from "src/core/products/api/products.api";
import { IStore } from "src/core/shared/store";

import { IList } from "../api/lists.api";

/**
 * Check if an order could be submitted from this list
 *
 * @param {IOrder} order - order object.
 * @param {IDrop} drop - drop object.
 *
 * @return {string | null} - The reason it didn't pass, or null if it does pass.
 */
const useCanSubmitStandingOrder = () => {
  const [ { drop } ] = useDrop();
  const validAgainstProductRelatedGroups = useValidAgainstProductRelatedGroups();

  const {
    terminology,
    packSizeConstraints,
    orderVariants
  } = useMappedState(({ global }: IStore) => ({
    terminology: global?.settings?.terminology,
    packSizeConstraints: global?.settings?.packSizeConstraints,
    orderVariants: global?.settings?.orderVariants || []
  }));

  const canSubmitStandingOrder = React.useCallback(
    (list: IList | null): string | null => {
      if (!drop) {
        return "Drop not found, please exit the order before continuing.";
      }

      const deliveryRoute = getDeliveryRoute(drop.groupedSettings);
      const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
      const minimumOrderValue = getMinimumOrderValue(drop.groupedSettings);
      const minimumOrderQuantitySetting = getMinimumOrderQuantitySetting(drop.groupedSettings);
      const orderMode = getOrderMode(drop.groupedSettings);
      const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);

      if (
        !list ||
        !drop.groupedSettings ||
        !minimumOrderValue ||
        !leadTimes ||
        !deliveryRoute ||
        !minimumOrderQuantitySetting ||
        !orderMode ||
        !defaultCutoff
      ) {
        return "Order cannot be submitted";
      }

      const { message, errorLevel } = validAgainstProductRelatedGroups(list.listLines);

      if (errorLevel === ErrorLevel.Error) {
        return message;
      }
      const productsValid = _.every(list.listLines, ll => canListOrderProduct(ll.product, list.schedule) === null);

      if (!productsValid) {
        return "Standing order cannot be submitted, as some products contain errors";
      }

      const PONumberValid = poNumberIsValid(list.customerReference || "", drop);

      if (PONumberValid) {
        return `Standing order cannot be submitted without a PO number, ${PONumberValid}`;
      }

      // if min value is not met -> Fail
      if (orderMinimumValueNotMet(list, minimumOrderValue)) {
        return `Standing order cannot be submitted, as the minimum list value of £${minimumOrderValue.value} was not met`;
      }

      // if no order lines -> Fail
      if (list.listLines.length === 0) {
        return "Standing orders must contain at least one product to be submitted.";
      }

      const minOrderQtyError = orderMinimumQuantityMet(list, minimumOrderQuantitySetting, terminology);

      // if min quantity is not met -> Fail
      if (minOrderQtyError) {
        return minOrderQtyError;
      }

      // if a list includes a product below minimum order quantity - FAIL
      const productMinimumQuantityFailed = productMinimumQuantityNotMet(list.listLines);

      if (productMinimumQuantityFailed) {
        return productMinimumQuantityFailed;
      }

      // validate order against pack size constraints
      const orderConstraintViolation = validateAgainstMaxPackSizeConstraints(list, orderVariants, packSizeConstraints, terminology);

      // if constraint violated - FAIL
      if (orderConstraintViolation) {
        return orderConstraintViolation;
      }

      return null;
    },
    [
      drop,
      orderVariants,
      terminology,
      packSizeConstraints,
      validAgainstProductRelatedGroups
    ]
  );

  return canSubmitStandingOrder;
};

export default useCanSubmitStandingOrder;
